import React, { useEffect, useState, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
} from 'chart.js';
import 'chartjs-adapter-date-fns'; // Import date adapter

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale
);

const activityTypes = {
  BRAINDUMP_NEW: 'Braindump (Nieuw)',
  FLASHCARD_NEW: 'Flashcard (Nieuw)',
  FLASHCARD_REPEAT: 'Flashcard (Herhaling)',
  MC_NEW: 'MC Vraag (Nieuw)',
  MC_REPEAT: 'MC Vraag (Herhaling)',
  KNOWLEDGE_CHECK_NEW: 'Check je Kennis (Nieuw)',
  KNOWLEDGE_CHECK_REPEAT: 'Check je Kennis (Herhaling)',
  CLINICAL_REASONING_NEW: 'Klinisch Redeneren (Nieuw)',
  CLINICAL_REASONING_REPEAT: 'Klinisch Redeneren (Herhaling)',
};

const activityColors = {
  [activityTypes.BRAINDUMP_NEW]: '#3B82F6', // Blauw
  [activityTypes.FLASHCARD_NEW]: '#10B981', // Groen
  [activityTypes.FLASHCARD_REPEAT]: '#6EE7B7', // Lichtgroen
  [activityTypes.MC_NEW]: '#F59E0B', // Oranjegeel
  [activityTypes.MC_REPEAT]: '#FCD34D', // Licht oranjegeel
  [activityTypes.KNOWLEDGE_CHECK_NEW]: '#D97706', // Donkergeel/Mosterd
  [activityTypes.KNOWLEDGE_CHECK_REPEAT]: '#FDE68A', // Lichtgeel
  [activityTypes.CLINICAL_REASONING_NEW]: '#8B5CF6', // Paars
  [activityTypes.CLINICAL_REASONING_REPEAT]: '#C4B5FD', // Lichtpaars
};

// Helper functies voor datummanipulatie (vermijd externe lib voor eenvoud hier)
const getStartOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const ActivityChart = () => {
  const [activityData, setActivityData] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const dailyChartRef = useRef(null);
  const weeklyChartRef = useRef(null);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    const loadAndProcessData = () => {
      const सातDinPehele = new Date();
      सातDinPehele.setDate(सातDinPehele.getDate() - 7);
      सातDinPehele.setHours(0,0,0,0);

      const teenMaheenePehle = new Date();
      teenMaheenePehle.setMonth(teenMaheenePehle.getMonth() - 3);
      teenMaheenePehle.setHours(0,0,0,0);

      const allActivities = [];

      // 1. Braindumps
      try {
        const braindumps = JSON.parse(localStorage.getItem('basisBraindumps') || '[]');
        braindumps.forEach(bd => {
          if (bd.date) {
            allActivities.push({ timestamp: new Date(bd.date), type: activityTypes.BRAINDUMP_NEW });
          }
        });
      } catch (e) { console.error("Error parsing braindumps:", e); }

      // 2. Flashcards
      try {
        const flashcardAssessments = JSON.parse(localStorage.getItem('flashcardAssessments') || '{}');
        // flashcardAssessments is nu van de vorm: { cardId1: [{level, timestamp}, ...], cardId2: [...] }
        Object.entries(flashcardAssessments).forEach(([cardId, assessmentArray]) => {
          if (Array.isArray(assessmentArray) && assessmentArray.length > 0) {
            // Sorteer de beoordelingen voor deze kaart op timestamp
            const sortedAssessments = [...assessmentArray].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            
            sortedAssessments.forEach((assessmentEntry, index) => {
              if (assessmentEntry && assessmentEntry.timestamp) {
                allActivities.push({
                  timestamp: new Date(assessmentEntry.timestamp),
                  type: index === 0 ? activityTypes.FLASHCARD_NEW : activityTypes.FLASHCARD_REPEAT,
                });
              }
            });
          }
        });
      } catch (e) { console.error("Error parsing or processing flashcardAssessments:", e); }

      // 3. MC Vragen
      try {
        const mcScores = JSON.parse(localStorage.getItem('mcScores') || '{}');
        Object.values(mcScores).forEach(attempts => {
          if (Array.isArray(attempts)) {
            attempts.forEach((attempt, index) => {
              if (attempt && attempt.timestamp) {
                allActivities.push({
                  timestamp: new Date(attempt.timestamp),
                  type: index === 0 ? activityTypes.MC_NEW : activityTypes.MC_REPEAT,
                });
              }
            });
          }
        });
      } catch (e) { console.error("Error parsing mcScores:", e); }
      
      // 4. Check je Kennis (uitlegAnswerVersions)
      try {
        const uitlegAnswerVersions = JSON.parse(localStorage.getItem('uitlegAnswerVersions') || '{}');
        Object.values(uitlegAnswerVersions).forEach(versions => {
          if (Array.isArray(versions)) {
            versions.forEach((version, index) => {
              if (version && version.timestamp) {
                allActivities.push({
                  timestamp: new Date(version.timestamp),
                  type: index === 0 ? activityTypes.KNOWLEDGE_CHECK_NEW : activityTypes.KNOWLEDGE_CHECK_REPEAT,
                });
              }
            });
          }
        });
      } catch (e) { console.error("Error parsing uitlegAnswerVersions:", e); }

      // 5. Klinisch Redeneren (toepassenAnswerVersions)
      try {
        const toepassenAnswerVersions = JSON.parse(localStorage.getItem('toepassenAnswerVersions') || '{}');
        Object.values(toepassenAnswerVersions).forEach(versions => {
          if (Array.isArray(versions)) {
            versions.forEach((version, index) => {
              if (version && version.timestamp) {
                allActivities.push({
                  timestamp: new Date(version.timestamp),
                  type: index === 0 ? activityTypes.CLINICAL_REASONING_NEW : activityTypes.CLINICAL_REASONING_REPEAT,
                });
              }
            });
          }
        });
      } catch (e) { console.error("Error parsing toepassenAnswerVersions:", e); }

      // Filter activities for the last 7 days (daily chart)
      const dailyActivities = allActivities.filter(act => act.timestamp >= सातDinPehele);
      const dailyCounts = {}; // Format: { 'YYYY-MM-DD': { 'ActivityType': count } }

      for (let d = new Date(सातDinPehele); d <= new Date(); d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        dailyCounts[dateStr] = {};
        Object.values(activityTypes).forEach(type => dailyCounts[dateStr][type] = 0);
      }
      
      dailyActivities.forEach(act => {
        const dateStr = getStartOfDay(act.timestamp).toISOString().split('T')[0];
        if (dailyCounts[dateStr]) { //Ensure the date string is a key in dailyCounts
            dailyCounts[dateStr][act.type] = (dailyCounts[dateStr][act.type] || 0) + 1;
        }
      });

      const dailyLabels = Object.keys(dailyCounts).sort();
      const dailyDatasets = Object.values(activityTypes).map(type => ({
        label: type,
        data: dailyLabels.map(label => dailyCounts[label][type] || 0),
        backgroundColor: activityColors[type],
      }));
      
      // Filter activities for the last 3 months (weekly chart)
      const weeklyActivities = allActivities.filter(act => act.timestamp >= teenMaheenePehle);
      const weeklyCounts = {}; // Format: { 'YYYY-MM-DD_start_of_week': { 'ActivityType': count } }

      // Initialize weeklyCounts for all weeks in the last 3 months
      let currentWeekStart = getStartOfWeek(teenMaheenePehle);
      const today = new Date();
      while(currentWeekStart <= today) {
        const weekStr = currentWeekStart.toISOString().split('T')[0];
        weeklyCounts[weekStr] = {};
        Object.values(activityTypes).forEach(type => weeklyCounts[weekStr][type] = 0);
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }

      weeklyActivities.forEach(act => {
        const weekStr = getStartOfWeek(act.timestamp).toISOString().split('T')[0];
        if(weeklyCounts[weekStr]) { //Ensure the week string is a key in weeklyCounts
            weeklyCounts[weekStr][act.type] = (weeklyCounts[weekStr][act.type] || 0) + 1;
        }
      });

      const weeklyLabels = Object.keys(weeklyCounts).sort();
      const weeklyDatasets = Object.values(activityTypes).map(type => ({
        label: type,
        data: weeklyLabels.map(label => weeklyCounts[label][type] || 0),
        backgroundColor: activityColors[type],
      }));

      setActivityData({
        daily: { labels: dailyLabels, datasets: dailyDatasets },
        weekly: { labels: weeklyLabels, datasets: weeklyDatasets },
      });
      setChartKey(prevKey => prevKey + 1);
    };

    loadAndProcessData();
  }, []);

  // Add effect to detect when charts are ready
  useEffect(() => {
    if (activityData) {
      // Give charts time to render
      const timer = setTimeout(() => {
        setChartsReady(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [activityData]);

  useEffect(() => {
    // Expose refs globally for PDF generation
    if (chartsReady) {
      window._activityCharts = {
        daily: dailyChartRef.current,
        weekly: weeklyChartRef.current,
      };
    }
  }, [chartsReady]);

  if (!activityData) {
    return <p>Activiteitengrafiek wordt geladen...</p>;
  }

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Activiteit Afgelopen Week (Dagelijks)',
      },
    },
    scales: {
      x: {
        stacked: false,
        type: 'time',
        time: {
          unit: 'day',
          displayFormats: {
            day: 'E dd-MM' // bv. Ma 13-05
          },
          tooltipFormat: 'PPP' // bv. May 13, 2024
        },
        title: {
          display: true,
          text: 'Datum'
        }
      },
      y: {
        stacked: false,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Aantal Activiteiten'
        },
        ticks: {
            stepSize: 1, // Toon alleen gehele getallen op de y-as
            callback: function(value) {if (Number.isInteger(value)) {return value;}}
        }
      },
    },
  };

  const weeklyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Activiteit Afgelopen 3 Maanden (Wekelijks)',
      },
    },
    scales: {
      x: {
        stacked: false,
        type: 'time',
        time: {
          unit: 'week',
          displayFormats: {
            week: "'Week' ww - yyyy" // Gebruik dubbele quotes voor de string, enkele voor 'Week'
          },
          tooltipFormat: "'Week' ww, yyyy" // Gebruik dubbele quotes voor de string, enkele voor 'Week'
        },
        title: {
          display: true,
          text: 'Week'
        }
      },
      y: {
        stacked: false,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Aantal Activiteiten'
        },
        ticks: {
            stepSize: 1, // Toon alleen gehele getallen op de y-as
            callback: function(value) {if (Number.isInteger(value)) {return value;}}
        }
      },
    },
  };
  
  // Tijdelijke check om rendering error te voorkomen als labels nog leeg zijn
  const noDailyData = !activityData.daily || !activityData.daily.labels || activityData.daily.labels.length === 0;
  const noWeeklyData = !activityData.weekly || !activityData.weekly.labels || activityData.weekly.labels.length === 0;


  return (
    <div id="activiteiten-overzicht-chart" className="space-y-8 mt-8">
      <div className="p-4 bg-white rounded-lg shadow">
        {noDailyData ? (
          <p className="text-center text-gray-500">Geen dagelijkse activiteit geregistreerd voor de afgelopen week.</p>
        ) : (
          <div style={{ height: '400px' }} ref={dailyChartRef} data-chart-ready={chartsReady}>
            <Bar key={`daily-chart-${chartKey}`} options={dailyChartOptions} data={activityData.daily} />
          </div>
        )}
      </div>
      <div className="p-4 bg-white rounded-lg shadow">
         {noWeeklyData ? (
          <p className="text-center text-gray-500">Geen wekelijkse activiteit geregistreerd voor de afgelopen 3 maanden.</p>
        ) : (
          <div style={{ height: '400px' }} ref={weeklyChartRef} data-chart-ready={chartsReady}>
            <Bar key={`weekly-chart-${chartKey}`} options={weeklyChartOptions} data={activityData.weekly} />
          </div>
        )}
      </div>
    </div>
  );
};

// Update the getChartElements method to use window refs
ActivityChart.getChartElements = async () => {
  // Wait until refs are set (max 2s)
  for (let i = 0; i < 20; i++) {
    if (window._activityCharts?.daily && window._activityCharts?.weekly) {
      return {
        dailyChartElement: window._activityCharts.daily,
        weeklyChartElement: window._activityCharts.weekly,
      };
    }
    await new Promise(res => setTimeout(res, 100));
  }
  return { dailyChartElement: null, weeklyChartElement: null };
};

export default ActivityChart; 